import { useTranslation } from 'react-i18next';
import { Alert } from '../components/kit.tsx';
import { SafeLink } from '../components/SafeLink.tsx';
import { signInUrl } from '../lib/api.ts';
import { usePageTitle } from '../lib/format.ts';
import { messageText } from '../lib/i18n.ts';
import { useRouter } from '../lib/router.tsx';

/**
 * Sign-in (roadmap 2.1, 3.4). Two equal choices: the person's own device, or a shared / lab computer,
 * which signs out after 15 minutes idle and leaves nothing behind (spec §1).
 */
export function Login({ reasonKey }: { reasonKey?: string | undefined }) {
  const { t } = useTranslation();
  const { path, search } = useRouter();
  usePageTitle(t('login.title'));
  const returnTo = path === '/login' ? (search.get('returnTo') ?? '/') : `${path}${search.size ? `?${search.toString()}` : ''}`;
  // /auth/callback sends failed browser sign-ins here with a message key (never free text)
  const errorKey = search.get('error');
  return (
    <div className="stack">
      <h1>{t('login.title')}</h1>
      {reasonKey ? <Alert tone="warning"><p>{messageText(reasonKey)}</p></Alert> : null}
      {errorKey && /^(err|auth)\.[a-zA-Z_.]+$/.test(errorKey) ? <Alert tone="error"><p>{messageText(errorKey)}</p></Alert> : null}
      <p>{t('login.intro')}</p>
      <section className="card card--emphasis" aria-labelledby="login-personal">
        <h2 id="login-personal">{t('login.personalTitle')}</h2>
        <p>{t('login.personalBody')}</p>
        <SafeLink document className="btn" href={signInUrl('standard', returnTo)}>{t('auth.signIn')}</SafeLink>
      </section>
      <section className="card card--emphasis" aria-labelledby="login-shared">
        <h2 id="login-shared">{t('login.sharedTitle')}</h2>
        <p>{t('login.sharedBody')}</p>
        <ul>
          <li>{t('login.sharedIdle')}</li>
          <li>{t('login.sharedNothingKept')}</li>
          <li>{t('login.sharedGoogle')}</li>
        </ul>
        <SafeLink document className="btn btn--secondary" href={signInUrl('shared', returnTo)}>{t('auth.sharedComputer')}</SafeLink>
      </section>
      <p className="muted">{t('auth.domainOnly')}</p>
      <p><SafeLink href="/install">{t('install.link')}</SafeLink></p>
    </div>
  );
}
